import { ReactElement } from 'react'
import * as Checkbox from '@radix-ui/react-checkbox'
import * as RadioGroup from '@radix-ui/react-radio-group'
import { Option } from 'components/atoms/inputs/Select/parts/types'
import { useFlexClassNames } from 'hooks/useClassNames'
import { BaseInputProps, InputWithOptionsType } from 'components/atoms/inputs/Input/Input'
import { useFormContext } from 'react-hook-form'

type InputOptionProps<OptionValue extends string> =
  & Omit<BaseInputProps, 'onBlur'> & {
    type: InputWithOptionsType
    option: Option<OptionValue>
  }

export const CheckboxOption = <OptionValue extends string>(props: InputOptionProps<OptionValue>): ReactElement | null => {
  const { onChange, option, type, ...inputProps } = props
  const { setValue } = useFormContext()

  return (
    <Checkbox.Root
      { ...inputProps }
      value={ inputProps.value as OptionValue }
      defaultChecked={ props.value === option.value }
      onCheckedChange={ (checked) => setValue(props.name, checked) }
      id={ option.value }
    >
      <Checkbox.Indicator />
    </Checkbox.Root>
  )
}

export const RadioOption = <OptionValue extends string>({ option: { value }}: InputOptionProps<OptionValue>): ReactElement | null => (
  <RadioGroup.Item id={ value } value={ value }>
    <RadioGroup.Indicator />
  </RadioGroup.Item>
)

export const LabeledOption = <OptionValue extends string>(props: InputOptionProps<OptionValue>): ReactElement | null => {
  const { type, option: { label, value, note }} = props
  const inputOption = type === 'checkboxes' ? <CheckboxOption { ...props } /> : <RadioOption { ...props } />
  const optionClassName = useFlexClassNames({ gap: 'tiny', horizontal: true, verticallyCenter: true, shrinkWidth: true })
  return (
    <label htmlFor={ value } className={ optionClassName }>
      { inputOption }
      <div>
        { label }
        { !!note && <><br /><small>{ note }</small></> }
      </div>
    </label>
  )
}
